import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Stack } from "@mui/material";
import useDocumentTitle from "../../../hooks/useDocumentTitle";
import { useGetCreditNoteHandler } from "../dataHandlers/getCreditNoteHandler";
import CreditNoteForm from "../components/CreditNoteForm";
import { useResubmitCreditNoteHandler } from "../dataHandlers/resubmitCreditNoteHandler";
import DocumentSkeletonForm from "../../../components/DocumentSkeletonForm";
import { usePermissions } from '../../../hooks/usePermissions';
import { UPDATE_PURCHASE_CREDIT_NOTE, UPDATE_SALE_CREDIT_NOTE } from '../../../constants/eInvoicePermissions';
import { useActionDeniedSnackbar } from '../../../hooks/useActionDeniedSnackbar';

export default function ResubmitCreditNotePage({ isPurchase = false }) {
  useDocumentTitle("Resubmit credit note");
  const navigate = useNavigate();
  const { document_id } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { loading, hasEInvoicePermission } = usePermissions();
  const showActionDeniedSnackbar = useActionDeniedSnackbar();
  const { creditNote, isLoading } = useGetCreditNoteHandler(
    document_id,
    isPurchase,
  );

  const onCreditNoteResubmitted = () => {
    navigate(
      isPurchase ? "/purchases?tab=credit-notes" : "/sales?tab=credit-notes",
    );
  };

  const { onSubmit, error, isPending } = useResubmitCreditNoteHandler({
    onSuccess: onCreditNoteResubmitted,
    isPurchase,
  });

  useEffect(() => {
    if (loading) {
      return;
    }
    const updatePermission = isPurchase ? UPDATE_PURCHASE_CREDIT_NOTE : UPDATE_SALE_CREDIT_NOTE;
    if (!hasEInvoicePermission(updatePermission)) {
      showActionDeniedSnackbar();
      navigate(isPurchase ? '/purchases/documents?tab=credit-notes' : '/sales/documents?tab=credit-notes', {
        replace: true,
      });
    }
  }, [isPurchase, loading, hasEInvoicePermission, showActionDeniedSnackbar, navigate]);

  useEffect(() => {
    if (!isPending && isSubmitting) {
      setIsSubmitting(false);
    }
  }, [isPending, isSubmitting]);

  const onResubmit = async (data) => {
    if (!creditNote?.uuid) {
      return;
    }
    setIsSubmitting(true);
    const payload = {
      ...data,
      status: "active",
    };
    try {
      await onSubmit(payload, creditNote.uuid);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onCancel = () => {
    navigate(
      isPurchase ? "/purchases?tab=credit-notes" : "/sales?tab=credit-notes",
    );
  };

  if (isLoading || !creditNote) {
    return <DocumentSkeletonForm />;
  }

  return (
    <Stack direction="column" sx={{ mt: "-2rem" }}>
      <CreditNoteForm
        onSubmit={onResubmit}
        onCancel={onCancel}
        initialData={creditNote}
        isPurchase={isPurchase}
        isResubmit={true}
        apiErrors={error ?? {}}
        isLoading={isPending || isSubmitting}
      />
    </Stack>
  );
}
